import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/blogs").then(({ data }) => setBlogs(data));
  }, []);

  const cats = ["All", "International", "UK Cities"];
  const filtered = filter === "All" ? blogs : blogs.filter((b) => b.category === filter);
  const intl = blogs.filter((b) => b.category === "International");

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
      <SEO
        title="Pet Transport Journal — 60 UK & EU route guides"
        description="Route-by-route pet transport guides for every major UK city and European destination. Prices, ferry vs Eurotunnel, border paperwork, and local vet tips."
        path="/blog"
      />
      <div className="max-w-3xl">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">The Journal</span>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-3">Routes, prices, &amp; pet travel guides.</h1>
        <p className="text-lg text-muted-foreground mt-5">
          60 city &amp; cross-channel guides covering the UK and Europe — airports, capitals, and every major destination.
        </p>
        <div className="mt-6">
          <Link to="/routes" className="text-sm text-accent hover:underline underline-offset-4" data-testid="blog-to-routes-hub">
            Or see every route at a glance →
          </Link>
        </div>
      </div>

      {/* Featured international */}
      {intl.length > 0 && filter === "All" && (
        <section className="mt-16">
          <h2 className="font-display text-2xl mb-6">Featured · International routes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {intl.slice(0, 3).map((b) => (
              <Link key={b.slug} to={`/blog/${b.slug}`} className="group" data-testid={`blog-featured-${b.slug}`}>
                <Card className="overflow-hidden border-border h-full">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={b.image_url} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs uppercase tracking-wider text-accent font-semibold">{b.route_from} → {b.route_to}</span>
                    <h3 className="font-display text-lg mt-2">{b.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{b.excerpt}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Filter */}
      <div className="flex gap-3 mt-16 mb-8">
        {cats.map((c) => (
          <Button
            key={c}
            variant={filter === c ? "default" : "outline"}
            onClick={() => setFilter(c)}
            size="sm"
            data-testid={`blog-filter-${c.toLowerCase().replace(" ", "-")}`}
          >
            {c}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((b) => (
          <Link key={b.slug} to={`/blog/${b.slug}`} className="group" data-testid={`blog-card-${b.slug}`}>
            <Card className="overflow-hidden border-border h-full hover:shadow-md transition-all">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={b.image_url} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <span className="text-xs uppercase tracking-wider text-accent font-semibold">{b.category}</span>
                <h3 className="font-display text-lg mt-2 leading-snug">{b.title}</h3>
                <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{b.excerpt}</p>
                <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">Read article <ArrowRight size={12} /></p>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
